import type { ThemeData } from "@/AppRedux/actions/theme/themeType";

export const themeDefault: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#1677ff",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorError: "#ff4d4f",
    colorInfo: "#1677ff",

    colorBgBase: "#ffffff",
    colorTextBase: "#141414",

    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
    motion: true,
  },
  components: {
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#141414",
      itemHoverColor: "#1677ff",
      itemSelectedColor: "#1677ff",
      horizontalItemHoverColor: "#1677ff",
      horizontalItemSelectedColor: "#1677ff",
    },
    Tabs: {
      itemActiveColor: "#1677ff",
      itemHoverColor: "#4096ff",
    },
  },
};

// Pastel Mint Breeze
export const themePastelMint: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#7CD1B8",
    colorSuccess: "#6ED6A0",
    colorWarning: "#FCD277",
    colorError: "#F79B9B",
    colorInfo: "#80BFFF",
    colorBgBase: "#FAFFFD",
    colorTextBase: "#1F2A37",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  },
  components: {
    Layout: { headerBg: "#E8FBF6", headerColor: "#0F172A" },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#0F172A",
      itemHoverColor: "#7CD1B8",
      itemSelectedColor: "#7CD1B8",
      horizontalItemHoverColor: "#7CD1B8",
      horizontalItemSelectedColor: "#7CD1B8",
    },
    Select: {
      optionSelectedColor: "#0F172A",
      optionSelectedBg: "rgba(124, 209, 184, 0.18)",
    },
    Drawer: {
      colorBgElevated: "#FAFFFD",
    },
    DatePicker: {
      activeBorderColor: "#7CD1B8",
      cellSelectedBg: "rgba(124, 209, 184, 0.18)",
    },
  },
};

// Pastel Lavender Dream
export const themePastelLavender: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#B9A7F9",
    colorSuccess: "#A7EFC3",
    colorWarning: "#F9E0AE",
    colorError: "#F5B8C5",
    colorInfo: "#C7D2FE",
    colorBgBase: "#F9FAFF",
    colorTextBase: "#1F2937",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily:
      "'Poppins', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  },
  components: {
    Layout: { headerBg: "#F3F0FF", headerColor: "#1F2937" },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#1F2937",
      itemHoverColor: "#B9A7F9",
      itemSelectedColor: "#B9A7F9",
      horizontalItemHoverColor: "#B9A7F9",
      horizontalItemSelectedColor: "#B9A7F9",
    },
    Select: {
      optionSelectedColor: "#1F2937",
      optionSelectedBg: "rgba(185, 167, 249, 0.18)",
    },
    Drawer: {
      colorBgElevated: "#F9FAFF",
    },
    DatePicker: {
      activeBorderColor: "#B9A7F9",
      cellSelectedBg: "rgba(185, 167, 249, 0.18)",
    },
  },
};

// Pastel Peach Sunset
export const themePastelPeach: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#FFB5A7",
    colorSuccess: "#A8E6CF",
    colorWarning: "#FFD6A5",
    colorError: "#FFADAD",
    colorInfo: "#BDE0FE",
    colorBgBase: "#FFF8F6",
    colorTextBase: "#2A2E34",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily:
      "'Nunito Sans', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  },
  components: {
    Layout: { headerBg: "#FFF1EC", headerColor: "#2A2E34" },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#2A2E34",
      itemHoverColor: "#FFB5A7",
      itemSelectedColor: "#FFB5A7",
      horizontalItemHoverColor: "#FFB5A7",
      horizontalItemSelectedColor: "#FFB5A7",
    },
    Select: {
      optionSelectedColor: "#2A2E34",
      optionSelectedBg: "rgba(255, 181, 167, 0.18)",
    },
    Drawer: {
      colorBgElevated: "#FFF8F6",
    },
    DatePicker: {
      activeBorderColor: "#FFB5A7",
      cellSelectedBg: "rgba(255, 181, 167, 0.18)",
    },
  },
};

// Pastel Ocean Mist
export const themePastelOcean: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#9AD0EC",
    colorSuccess: "#A7E9C9",
    colorWarning: "#FFE5B4",
    colorError: "#F8B4B4",
    colorInfo: "#A7C7E7",
    colorBgBase: "#F5FBFF",
    colorTextBase: "#1E293B",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily:
      "'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  },
  components: {
    Layout: { headerBg: "#EDF7FF", headerColor: "#1E293B" },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#1E293B",
      itemHoverColor: "#9AD0EC",
      itemSelectedColor: "#9AD0EC",
      horizontalItemHoverColor: "#9AD0EC",
      horizontalItemSelectedColor: "#9AD0EC",
    },
    Select: {
      optionSelectedColor: "#1E293B",
      optionSelectedBg: "rgba(154, 208, 236, 0.18)",
    },
    Drawer: {
      colorBgElevated: "#F5FBFF",
    },
    DatePicker: {
      activeBorderColor: "#9AD0EC",
      cellSelectedBg: "rgba(154, 208, 236, 0.18)",
    },
  },
};
// --- Extra: Light and Dark presets --- //

export const themeLightPastel: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#4096ff",
    colorSuccess: "#4570e7ff",
    colorWarning: "#FFE3A7",
    colorError: "#b90226ff",
    colorInfo: "#A7C8FF",
    colorBgBase: "#FFFFFF",
    colorTextBase: "#000000ff",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: { headerBg: "#4570e7ff", headerColor: "#FFFFFF" },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#FFFFFF",
      itemHoverColor: "#FFFFFF",
      itemSelectedColor: "lightskyblue",
      horizontalItemHoverColor: "#8EC5B6",
      horizontalItemSelectedColor: "#90c6f8ff",
    },
    Select: {
      colorText: "#FFFFFF",
      colorBgElevated: "#4570e7ff",
      colorTextPlaceholder: "#FFFFFF",
      optionSelectedColor: "#4096ff",
      optionSelectedBg: "#FFFFFF",
    },
    Drawer: {
      colorBgElevated: "#FFFFFF",
    },
    DatePicker: {
      activeBorderColor: "#8EC5B6",
      cellSelectedBg: "rgba(142, 197, 182, 0.18)",
    },
  },
};

export const themeDarkPastel: ThemeData = {
  algorithm: "dark",
  token: {
    colorPrimary: "#93C5FD",
    colorSuccess: "#6EE7B7",
    colorWarning: "#FCD34D",
    colorError: "#ffffffff",
    colorInfo: "#A5B4FC",
    colorBgBase: "#0F172A",
    colorTextBase: "#E5E7EB",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily:
      "'Plus Jakarta Sans', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  },
  components: {
    Layout: { headerBg: "#0B1220", headerColor: "#E5E7EB" },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#E5E7EB",
      itemHoverColor: "#93C5FD",
      itemSelectedColor: "#93C5FD",
      horizontalItemHoverColor: "#93C5FD",
      horizontalItemSelectedColor: "#93C5FD",
    },
    Select: {
      optionSelectedColor: "#E5E7EB",
      optionSelectedBg: "rgba(147, 197, 253, 0.18)",
    },
    Drawer: {
      colorBgElevated: "#0F172A",
    },
    DatePicker: {
      activeBorderColor: "#93C5FD",
      cellSelectedBg: "rgba(147, 197, 253, 0.18)",
    }
  },
};

export const themeDefaultLight: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#1677ff",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorError: "#ff4d4f",
    colorInfo: "#1677ff",

    colorBgBase: "#ffffff",
    colorTextBase: "#141414",

    borderRadius: 12,
    fontFamily:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    motion: true,
  },
  components: {
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      colorBgContainer: "#ffffff",
      itemColor: "#141414",
      itemHoverColor: "#1677ff",
      itemSelectedColor: "#1677ff",
      itemBg: "#ffffff",
      itemHoverBg: "#f5f5f5",
      itemSelectedBg: "#f0f7ff",
      horizontalItemHoverColor: "#1677ff",
      horizontalItemSelectedColor: "#1677ff",
    },Select: {
      optionSelectedColor: "#E5E7EB",
      optionSelectedBg: "rgba(147, 197, 253, 0.18)",
    },
    Drawer: {
      colorBgElevated: "#0F172A",
    },
    DatePicker: {
      activeBorderColor: "#93C5FD",
      cellSelectedBg: "rgba(147, 197, 253, 0.18)",
    }
  },
};

// ========================================
// 🌅 THEME 1: SUNSET ORANGE
// เหมาะสำหรับ: E-commerce, Food & Beverage, Creative Agency
// ========================================
export const themeSunsetOrange: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#FF6B35",
    colorSuccess: "#06D6A0",
    colorWarning: "#FFB627",
    colorError: "#EF233C",
    colorInfo: "#FFD23F",
    colorBgBase: "#FFF8F3",
    colorTextBase: "#2B2D42",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: {
      headerBg: "#FF6B35",
      headerColor: "#FFFFFF",
      bodyBg: "#FFF8F3",
    },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#FFFFFF",
      itemHoverColor: "#FFF8F3",
      itemSelectedColor: "#FFD23F",
      horizontalItemHoverColor: "#FF8C61",
      horizontalItemSelectedColor: "#FFD23F",
    },
    Select: {
      colorText: "#2B2D42",
      colorBgElevated: "#FFFFFF",
      colorTextPlaceholder: "#8D99AE",
      optionSelectedColor: "#FF6B35",
      optionSelectedBg: "#FFE8DF",
    },
    Drawer: {
      colorBgElevated: "#FFFFFF",
    },
    DatePicker: {
      activeBorderColor: "#FF6B35",
      cellSelectedBg: "rgba(255, 107, 53, 0.15)",
    },
  },
};

// ========================================
// 🌙 THEME 2: MIDNIGHT DARK
// เหมาะสำหรับ: Tech, SaaS, Dashboard, Gaming
// ========================================
export const themeMidnightDark: ThemeData = {
  algorithm: "dark",
  token: {
    colorPrimary: "#6C63FF",
    colorSuccess: "#00D9A5",
    colorWarning: "#FFC107",
    colorError: "#FF5252",
    colorInfo: "#40C4FF",
    colorBgBase: "#1A1A2E",
    colorTextBase: "#EAEAEA",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: {
      headerBg: "#16213E",
      headerColor: "#EAEAEA",
      bodyBg: "#1A1A2E",
    },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#EAEAEA",
      itemHoverColor: "#6C63FF",
      itemSelectedColor: "#FFFFFF",
      horizontalItemHoverColor: "#6C63FF",
      horizontalItemSelectedColor: "#FFFFFF",
      itemBg: "#16213E",
      itemSelectedBg: "rgba(108, 99, 255, 0.2)",
    },
    Select: {
      colorText: "#EAEAEA",
      colorBgElevated: "#16213E",
      colorTextPlaceholder: "#8B92A8",
      optionSelectedColor: "#FFFFFF",
      optionSelectedBg: "rgba(108, 99, 255, 0.3)",
    },
    Drawer: {
      colorBgElevated: "#16213E",
    },
    DatePicker: {
      activeBorderColor: "#6C63FF",
      cellSelectedBg: "rgba(108, 99, 255, 0.25)",
    },
  },
};

// ========================================
// 🌿 THEME 3: NATURE GREEN
// เหมาะสำหรับ: Health, Eco-Friendly, Organic Products
// ========================================
export const themeNatureGreen: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#2D6A4F",
    colorSuccess: "#52B788",
    colorWarning: "#F4A261",
    colorError: "#E76F51",
    colorInfo: "#74C69D",
    colorBgBase: "#F8FAF5",
    colorTextBase: "#1B4332",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: {
      headerBg: "#2D6A4F",
      headerColor: "#FFFFFF",
      bodyBg: "#F8FAF5",
    },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#FFFFFF",
      itemHoverColor: "#D8F3DC",
      itemSelectedColor: "#B7E4C7",
      horizontalItemHoverColor: "#52B788",
      horizontalItemSelectedColor: "#B7E4C7",
    },
    Select: {
      colorText: "#1B4332",
      colorBgElevated: "#FFFFFF",
      colorTextPlaceholder: "#74C69D",
      optionSelectedColor: "#2D6A4F",
      optionSelectedBg: "#D8F3DC",
    },
    Drawer: {
      colorBgElevated: "#FFFFFF",
    },
    DatePicker: {
      activeBorderColor: "#52B788",
      cellSelectedBg: "rgba(82, 183, 136, 0.2)",
    },
  },
};

// ========================================
// 💎 THEME 4: LUXURY PURPLE
// เหมาะสำหรับ: Beauty, Fashion, Premium Services
// ========================================
export const themeLuxuryPurple: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#9333EA",
    colorSuccess: "#10B981",
    colorWarning: "#F59E0B",
    colorError: "#DC2626",
    colorInfo: "#C084FC",
    colorBgBase: "#FAF5FF",
    colorTextBase: "#1F2937",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: {
      headerBg: "linear-gradient(135deg, #9333EA 0%, #C084FC 100%)",
      headerColor: "#FFFFFF",
      bodyBg: "#FAF5FF",
    },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#FFFFFF",
      itemHoverColor: "#F3E8FF",
      itemSelectedColor: "#FDE68A",
      horizontalItemHoverColor: "#C084FC",
      horizontalItemSelectedColor: "#FDE68A",
    },
    Select: {
      colorText: "#1F2937",
      colorBgElevated: "#FFFFFF",
      colorTextPlaceholder: "#A78BFA",
      optionSelectedColor: "#9333EA",
      optionSelectedBg: "#F3E8FF",
    },
    Drawer: {
      colorBgElevated: "#FFFFFF",
    },
    DatePicker: {
      activeBorderColor: "#C084FC",
      cellSelectedBg: "rgba(147, 51, 234, 0.15)",
    },
  },
};

// ========================================
// 🔥 THEME 5: BOLD RED
// เหมาะสำหรับ: News, Sports, Entertainment
// ========================================
export const themeBoldRed: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#E63946",
    colorSuccess: "#06D6A0",
    colorWarning: "#FFB627",
    colorError: "#C1121F",
    colorInfo: "#457B9D",
    colorBgBase: "#FFFFFF",
    colorTextBase: "#1D3557",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: {
      headerBg: "#E63946",
      headerColor: "#FFFFFF",
      bodyBg: "#F8F9FA",
    },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#FFFFFF",
      itemHoverColor: "#FFD23F",
      itemSelectedColor: "#FFD23F",
      horizontalItemHoverColor: "#F1FAEE",
      horizontalItemSelectedColor: "#FFFFFF",
      fontWeightStrong: 600,
    },
    Select: {
      colorText: "#1D3557",
      colorBgElevated: "#FFFFFF",
      colorTextPlaceholder: "#6C757D",
      optionSelectedColor: "#FFFFFF",
      optionSelectedBg: "#E63946",
    },
    Drawer: {
      colorBgElevated: "#FFFFFF",
    },
    DatePicker: {
      activeBorderColor: "#E63946",
      cellSelectedBg: "rgba(230, 57, 70, 0.15)",
    },
  },
};

// ========================================
// 🌊 THEME 6: OCEAN TEAL
// เหมาะสำหรับ: Travel, Lifestyle, Real Estate
// ========================================
export const themeOceanTeal: ThemeData = {
  algorithm: "default",
  token: {
    colorPrimary: "#14B8A6",
    colorSuccess: "#22C55E",
    colorWarning: "#F97316",
    colorError: "#EF4444",
    colorInfo: "#06B6D4",
    colorBgBase: "#F0FDFA",
    colorTextBase: "#134E4A",
    borderRadius: 12,
    fontFamilyUrl:
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@300;400;500;600;700&display=swap",
    fontFamily: "'IBM Plex Sans Thai', 'IBM Plex Sans', sans-serif",
  },
  components: {
    Layout: {
      headerBg: "#14B8A6",
      headerColor: "#FFFFFF",
      bodyBg: "#F0FDFA",
    },
    Button: { borderRadius: 10 },
    Card: { borderRadius: 14 },
    Input: { borderRadius: 10 },
    Menu: {
      itemColor: "#FFFFFF",
      itemHoverColor: "#CCFBF1",
      itemSelectedColor: "#FEF3C7",
      horizontalItemHoverColor: "#5EEAD4",
      horizontalItemSelectedColor: "#FEF3C7",
    },
    Select: {
      colorText: "#134E4A",
      colorBgElevated: "#FFFFFF",
      colorTextPlaceholder: "#5EEAD4",
      optionSelectedColor: "#14B8A6",
      optionSelectedBg: "#CCFBF1",
    },
    Drawer: {
      colorBgElevated: "#FFFFFF",
    },
    DatePicker: {
      activeBorderColor: "#14B8A6",
      cellSelectedBg: "rgba(20, 184, 166, 0.2)",
    },
  },
};

export const themeMockups: ThemeData[] = [
  themeDefault,
  themePastelMint,
  themePastelLavender,
  themePastelPeach,
  themePastelOcean,
  themeLightPastel,
  themeDarkPastel,
  themeSunsetOrange,
  themeMidnightDark,
  themeNatureGreen,
  themeLuxuryPurple,
  themeBoldRed,
  themeOceanTeal,
];

export default themeMockups;
